import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { Button, Tag, Modal, Input, Select, DatePicker, Table, Drawer } from 'antd';
import { COLORS } from '../../constants/colors';

// Global styles for Select dropdown arrow positioning
export const GlobalSelectStyles = createGlobalStyle`
  .ant-select-arrow {
    position: absolute !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 11px !important;
    height: 14px !important;
    width: 14px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    color: ${COLORS.textMuted} !important;
    font-size: 14px !important;
    line-height: 1 !important;
    margin-top: 0 !important;
    
    .anticon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      line-height: 1 !important;
      height: 14px !important;
      width: 14px !important;
    }
  }

  .members-actions-menu .ant-dropdown-menu {
    min-width: 260px;
    padding: 8px !important;
    border-radius: 14px !important;
    border: 1px solid ${COLORS.border};
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06) !important;
  }

  .members-actions-menu .ant-dropdown-menu-item {
    display: flex !important;
    align-items: center;
    gap: 12px;
    padding: 10px 14px !important;
    margin: 2px 0 !important;
    border-radius: 10px !important;
    font-size: 14px !important;
    font-weight: 500;
    color: ${COLORS.textDark};
    line-height: 1.4 !important;
  }

  .members-actions-menu .ant-dropdown-menu-item .ant-dropdown-menu-item-icon,
  .members-actions-menu .ant-dropdown-menu-item svg {
    margin-inline-end: 0 !important;
    font-size: 14px !important;
    color: ${COLORS.textLight};
  }

  .members-actions-menu .ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover {
    background: ${COLORS.backgroundLight} !important;
  }

  .members-actions-menu .ant-dropdown-menu-item-danger,
  .members-actions-menu .ant-dropdown-menu-item-danger svg {
    color: #d4380d !important;
  }

  .members-actions-menu .ant-dropdown-menu-item-danger:not(.ant-dropdown-menu-item-disabled):hover {
    background: #fff2e8 !important;
  }

  .members-actions-menu .ant-dropdown-menu-item-divider {
    margin: 6px 6px !important;
    background-color: ${COLORS.border} !important;
  }
`;

// Animation keyframes
const slideInFromTop = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInFromLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 90px);
  overflow: hidden;

  /* On mobile the page flows naturally and the admin content area scrolls,
     so the member cards aren't trapped in a fixed-height, clipped box. */
  @media (max-width: 767px) {
    height: auto;
    overflow: visible;
  }

  // Global animation classes
  .header-animate-in {
    animation: ${slideInFromTop} 0.6s ease-out forwards;
  }

  .header-animate-out {
    opacity: 0;
    transform: translateY(-20px);
  }

  .filters-animate-in {
    animation: ${slideInFromLeft} 0.6s ease-out forwards;
  }

  .filters-animate-out {
    opacity: 0;
    transform: translateX(-20px);
  }

  .table-animate-in {
    animation: ${fadeInScale} 0.6s ease-out forwards;
  }

  .table-animate-out {
    opacity: 0;
    transform: scale(0.95);
  }
`;

export const PageTitle = styled.h1`
  margin: 0;
  color: ${COLORS.textDark};
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: -0.3px;
`;

export const ScreeningCountTag = styled.div`
  font-size: 14px;
  font-weight: 600;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const ActionButton = styled(Button)`
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  padding: 0 16px;
  font-weight: 600;
  border-radius: 8px;
  box-shadow: none !important;
  transition: all 0.2s ease;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &.ant-btn-primary {
    background: ${COLORS.$primaryBlue};
    border-color: ${COLORS.$primaryBlue};
    
    &:hover {
      background: ${COLORS.$primaryBlueHover};
      border-color: ${COLORS.$primaryBlueHover};
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3) !important;
    }
    
    &:active {
      background: ${COLORS.$primaryBlueDark};
      border-color: ${COLORS.$primaryBlueDark};
    }
  }
  
  &.ant-btn-ghost,
  &.ant-btn-default.ant-btn-background-ghost {
    background: transparent !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textMedium} !important;
    
    .anticon {
      color: ${COLORS.textMedium} !important;
    }
    
    &:hover,
    &.ant-btn-default.ant-btn-background-ghost:hover {
      background: transparent !important;
      border-color: ${COLORS.$primaryBlue} !important;
      color: ${COLORS.$primaryBlue} !important;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(24, 144, 255, 0.15) !important;
      
      .anticon {
        color: ${COLORS.$primaryBlue} !important;
      }
    }
    
    &:active,
    &.ant-btn-default.ant-btn-background-ghost:active {
      background: transparent !important;
      border-color: ${COLORS.$primaryBlueDark} !important;
      color: ${COLORS.$primaryBlueDark} !important;
      
      .anticon {
        color: ${COLORS.$primaryBlueDark} !important;
      }
    }
    
    &:focus,
    &.ant-btn-default.ant-btn-background-ghost:focus {
      background: transparent !important;
      border-color: ${COLORS.$primaryBlue} !important;
      color: ${COLORS.textMedium} !important;
      
      .anticon {
        color: ${COLORS.textMedium} !important;
      }
    }
  }
`;

export const FilterContainer = styled.div`
  margin-bottom: 24px;

  /* The list/spreadsheet toggle is desktop-only; mobile always shows cards. */
  .members-view-toggle {
    @media (max-width: 767px) {
      display: none;
    }
  }

  /* Actions: labeled dropdown on desktop, 3-dot bottom-sheet trigger on mobile. */
  .members-actions-desktop {
    @media (max-width: 767px) {
      display: none;
    }
  }

  .members-actions-mobile {
    display: none;

    @media (max-width: 767px) {
      display: block;
    }
  }
`;

// Square, outlined icon button used for the mobile "Actions" (3-dot) trigger.
export const MobileIconButton = styled(Button)`
  height: 40px !important;
  width: 40px !important;
  min-width: 40px !important;
  padding: 0 !important;
  border-radius: 10px !important;
  border: 1px solid ${COLORS.border} !important;
  background: #fff !important;
  color: ${COLORS.textDark} !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: none !important;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    border-color: ${COLORS.$primaryBlue} !important;
    color: ${COLORS.$primaryBlue} !important;
    background: #fff !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
  }
`;

// List of actions rendered inside the mobile bottom sheet.
export const ActionsSheetList = styled.div`
  display: flex;
  flex-direction: column;

  .divider {
    height: 1px;
    background: ${COLORS.border};
    margin: 6px 0;
  }

  button {
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 14px 4px;
    font-size: 16px;
    font-weight: 500;
    color: ${COLORS.textDark};
    text-align: left;

    .anticon,
    svg {
      color: ${COLORS.textLight};
      font-size: 16px;
      width: 18px;
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    &.danger,
    &.danger svg {
      color: #d4380d;
    }

    &:active:not(:disabled) {
      background: ${COLORS.backgroundLight};
    }
  }
`;


// Desktop-only wrapper for the members table / spreadsheet views.
export const DesktopMembersView = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (max-width: 767px) {
    display: none;
  }
`;

export const MobileMembersList = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
`;

export const MemberCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  padding: 14px 16px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:active { transform: scale(0.99); }

  .mc-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
  }

  .mc-title {
    font-size: 16px;
    font-weight: 700;
    color: ${COLORS.textDark};
    line-height: 1.3;
  }

  .mc-contact {
    margin-top: 6px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 14px;
    font-size: 13px;
    color: ${COLORS.textMedium};

    .anticon { color: ${COLORS.$primaryBlue}; margin-right: 2px; }
    .mc-demo { color: ${COLORS.textLight}; }
  }

  .mc-event {
    margin-top: 6px;
    font-size: 13px;
    color: ${COLORS.textLight};
  }

  .mc-status {
    margin-top: 10px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
`;

export const MobileMembersPagination = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 16px 0 4px;

    .pg-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      height: 44px;
      padding: 0 18px;
      border: 1px solid #e6e8ee;
      border-radius: 12px;
      background: #fff;
      color: ${COLORS.textDark};
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
      transition: all 0.15s ease;

      &:hover:not(:disabled) {
        border-color: ${COLORS.$primaryBlue};
        color: ${COLORS.$primaryBlue};
      }

      &:active:not(:disabled) { transform: scale(0.97); }

      &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
        box-shadow: none;
      }
    }

    .pg-info {
      flex: 1;
      text-align: center;
      font-size: 13px;
      font-weight: 600;
      color: ${COLORS.textLight};

      strong { color: ${COLORS.textDark}; }
    }
  }
`;

export const MobileFilterButton = styled(Button)`
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  min-width: 100px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  background: white !important;
  color: ${COLORS.textDark} !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: none !important;
  transition: all 0.2s ease;
  font-size: 14px !important;
  font-weight: 500 !important;
  gap: 8px !important;
  
  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    color: ${COLORS.$primaryBlue} !important;
    background: white !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
  }
  
  &:focus {
    border-color: ${COLORS.$primaryBlue} !important;
    color: ${COLORS.$primaryBlue} !important;
    background: white !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .anticon {
    font-size: 16px;
  }
`;

export const MobileFilterDrawer = styled(Drawer)`
  .ant-drawer-header {
    border-bottom: 1px solid ${COLORS.border};
    padding: 16px 24px;
    
    .ant-drawer-title {
      font-weight: 600;
      color: ${COLORS.textDark};
      font-size: 16px;
    }
  }
  
  .ant-drawer-body {
    padding: 24px;
    background: #fafafa;
  }
  
  .ant-drawer-close {
    color: ${COLORS.textMedium};
    
    &:hover {
      color: ${COLORS.textDark};
    }
  }
`;

export const StyledInput = styled(Input)`
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  box-shadow: none !important;
  font-size: 14px !important;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: none !important;
  }
  
  &:focus,
  &.ant-input-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-input-prefix {
    margin-right: 8px;
    
    .anticon {
      color: ${COLORS.textMuted};
      font-size: 16px;
    }
  }
`;

export const StyledSelect = styled(Select)`
  height: 40px !important;
  
  &.ant-select-sm {
    height: 40px !important;
    
    .ant-select-selector {
      height: 40px !important;
      padding: 0 11px !important;
      font-size: 14px !important;
    }
    
    .ant-select-selection-item {
      line-height: 38px !important;
      height: 38px !important;
      font-size: 14px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 38px !important;
      height: 38px !important;
      font-size: 14px !important;
    }
  }
  
  .ant-select-selector {
    height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: none !important;
    font-size: 14px !important;
    padding: 0 11px !important;
    display: flex !important;
    align-items: center !important;
    line-height: 1 !important;
    transition: all 0.2s ease;
    
    &:hover {
      border-color: ${COLORS.$primaryBlue} !important;
      box-shadow: none !important;
    }
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-select-selection-item {
    line-height: 38px !important;
    height: 38px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  .ant-select-selection-placeholder {
    line-height: 38px !important;
    height: 38px !important;
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
    color: ${COLORS.textMuted} !important;
  }
  
  .ant-select-arrow {
    color: ${COLORS.textMuted} !important;
    font-size: 14px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    right: 11px !important;
    height: 14px !important;
    margin-top: 0 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
`;

export const StyledRangePicker = styled(DatePicker.RangePicker)`
  height: 40px !important;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  box-shadow: none !important;
  font-size: 14px !important;
  padding: 0 11px !important;
  transition: all 0.2s ease;
  
  &.ant-picker-small {
    height: 40px !important;
    padding: 0 11px !important;
    font-size: 14px !important;
    
    .ant-picker-input {
      height: 38px !important;
      line-height: 38px !important;
      font-size: 14px !important;
      
      input {
        height: 38px !important;
        line-height: 38px !important;
        font-size: 14px !important;
      }
    }
    
    .ant-picker-range-separator {
      height: 38px !important;
      line-height: 38px !important;
      font-size: 14px !important;
    }
  }
  
  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: none !important;
  }
  
  &:focus,
  &.ant-picker-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }
  
  .ant-picker-input {
    height: 38px !important;
    line-height: 38px !important;
    font-size: 14px !important;
    
    input {
      height: 38px !important;
      line-height: 38px !important;
      font-size: 14px !important;
      
      &::placeholder {
        color: ${COLORS.textMuted} !important;
      }
    }
  }
  
  .ant-picker-range-separator {
    height: 38px !important;
    line-height: 46px !important;
    font-size: 14px !important;
    color: ${COLORS.textMuted} !important;
  }
  
  .ant-picker-suffix {
    color: ${COLORS.textMuted} !important;
    font-size: 14px !important;
  }
`;

export const StyledDatePicker = styled(DatePicker)`
  height: 40px !important;
  width: 100%;
  border-radius: 8px !important;
  border: 1px solid ${COLORS.border} !important;
  box-shadow: none !important;
  font-size: 14px !important;
  padding: 0 11px !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: none !important;
  }

  &:focus,
  &.ant-picker-focused {
    border-color: ${COLORS.$primaryBlue} !important;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
  }

  .ant-picker-input {
    height: 38px !important;

    input {
      height: 38px !important;
      line-height: 38px !important;
      font-size: 14px !important;

      &::placeholder {
        color: ${COLORS.textMuted} !important;
      }
    }
  }

  .ant-picker-suffix {
    color: ${COLORS.textMuted} !important;
    font-size: 14px !important;
  }
`;


export const TableContainer = styled.div`
  /* Animation support */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  
  /* Smooth fade-in animation for table content */
  &.table-fade-in {
    animation: ${fadeInScale} 0.5s ease-out forwards;
  }
  
  /* Loading state - fade out smoothly */
  &.table-loading {
    animation: ${fadeIn} 0.3s ease-out forwards;
  }
  
  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
  }
  
  /* Table header styling to match event management */
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 48px !important;
    padding: 12px 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    white-space: nowrap !important;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  /* Table body styling */
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    /* Alternating row colors for better readability */
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 12px 12px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 10px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    /* Allow horizontal scrolling on mobile */
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }

  /* Pagination styling - matching EventManagement */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          line-height: 28px !important;
        }
        
        .ant-select-arrow {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          right: 10px !important;
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 60px !important;
          text-align: center;
          font-weight: 600;
          transition: all 0.3s ease !important;
          line-height: 36px !important;
          padding: 0 0 0 8px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  /* Dropdown menu styling for pagination */
  .ant-select-dropdown {
    border-radius: 10px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    padding: 4px !important;
    
    .ant-select-item {
      border-radius: 8px !important;
      margin: 2px 0 !important;
      padding: 8px 12px !important;
      color: ${COLORS.textDark} !important;
      min-height: 32px !important;
      font-size: 13px !important;
      line-height: 1.4 !important;
      display: flex !important;
      align-items: center !important;
      
      &:hover {
        background-color: rgba(24, 144, 255, 0.08) !important;
        color: ${COLORS.$primaryBlue} !important;
      }
      
      &.ant-select-item-option-selected {
        background-color: ${COLORS.$primaryBlue} !important;
        color: white !important;
        font-weight: 600 !important;
        
        .ant-select-item-option-state {
          color: white !important;
        }
      }
    }
    
    .ant-select-item-option-state {
      color: ${COLORS.$primaryBlue} !important;
      font-weight: bold !important;
    }
  }
`;

export const StyledTable = styled(Table)`
  /* Animation support */
  animation-fill-mode: both;
  background: white;
  border-radius: 16px;
  border: 1px solid #f0f0f0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  /* Full-height scrollable table layout */
  &.ant-table-wrapper {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-spin-nested-loading {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;

    .ant-spin-container {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  }

  .ant-table {
    border-radius: 0;
    overflow: hidden;
    background: white;
    border: none;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-table-container {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .ant-table-body {
    flex: 1 !important;
    max-height: unset !important;
    overflow-y: auto !important;
  }
  
  .ant-table-thead > tr > th {
    background: white !important;
    color: ${COLORS.textDark} !important;
    border-bottom: 3px solid ${COLORS.$primaryBlue} !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    height: 48px !important;
    padding: 12px 12px !important;
    text-transform: uppercase !important;
    letter-spacing: 0.5px !important;
    position: relative;
    
    &:first-child {
      border-top-left-radius: 16px;
    }
    
    &:last-child {
      border-top-right-radius: 16px;
    }
  }
  
  .ant-table-tbody > tr {
    transition: all 0.2s ease;
    border-bottom: 1px solid #f5f5f5;
    
    &:hover > td {
      background: #fafbff !important;
      transform: translateY(-1px);
    }
    
    &:last-child {
      border-bottom: none;
      
      td {
        border-bottom: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
    
    /* Alternating row colors for better readability */
    &:nth-child(even) {
      background: #fafafa;
      
      &:hover > td {
        background: #f0f7ff !important;
      }
    }
  }
  
  .ant-table-tbody > tr > td {
    border-bottom: none;
    padding: 12px 12px;
    font-size: 14px;
    line-height: 1.5;
    vertical-align: middle;
    text-align: left;
    transition: all 0.2s ease;
  }

  /* Responsive column adjustments */
  @media (max-width: 1200px) {
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 10px;
      font-size: 13px;
    }
  }

  @media (max-width: 768px) {
    /* Allow horizontal scrolling on mobile */
    .ant-table {
      font-size: 12px;
    }
    
    .ant-table-thead > tr > th,
    .ant-table-tbody > tr > td {
      padding: 12px 8px;
      font-size: 12px;
      white-space: nowrap;
    }
    
    .ant-table-thead > tr > th {
      height: 48px !important;
      font-size: 11px !important;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
  }

  /* Pagination styling - matching EventManagement */
  .ant-pagination {
    margin: 0 !important;
    padding: 12px 16px !important;
    border-top: 1px solid ${COLORS.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .ant-pagination-total-text {
      font-size: 13px;
      color: ${COLORS.textMedium};
      font-weight: 500;
      flex: 1;
    }
    
    .ant-pagination-options {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      
      .ant-pagination-options-size-changer {
        .ant-select-selector {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          min-width: 100px !important;
          transition: all 0.3s ease !important;
          display: flex !important;
          align-items: center !important;
          padding: 0 28px 0 12px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
        }
        
        .ant-select-selection-item {
          height: 28px !important;
          padding: 0 !important;
          display: flex !important;
          align-items: center !important;
          font-weight: 500 !important;
          font-size: 13px !important;
          line-height: 28px !important;
        }
        
        .ant-select-arrow {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          right: 10px !important;
        }
        
        &.ant-select-focused .ant-select-selector {
          border-color: ${COLORS.$primaryBlue} !important;
          box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
        }
      }
      
      .ant-pagination-options-quick-jumper {
        display: flex;
        align-items: center;
        height: 32px;
        font-size: 13px;
        
        .ant-input {
          border-radius: 16px !important;
          border: 1.5px solid ${COLORS.border} !important;
          height: 32px !important;
          min-height: 32px !important;
          width: 60px !important;
          text-align: center;
          font-weight: 600;
          transition: all 0.3s ease !important;
          line-height: 36px !important;
          padding: 0 0 0 8px !important;
          
          &:hover {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
          }
          
          &:focus {
            border-color: ${COLORS.$primaryBlue} !important;
            box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.15) !important;
          }
        }
      }
    }
    
    .ant-pagination-item {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      a {
        color: ${COLORS.textDark};
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        text-decoration: none;
        font-size: 13px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        a {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-item-active {
        background: ${COLORS.$primaryBlue};
        border-color: ${COLORS.$primaryBlue};
        transform: translateY(-1px);
        
        a {
          color: white;
          font-weight: 700;
        }
        
        &:hover {
          background: ${COLORS.$primaryBlueDark};
          border-color: ${COLORS.$primaryBlueDark};
          
          a {
            color: white;
          }
        }
      }
    }
    
    .ant-pagination-prev,
    .ant-pagination-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textDark};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
      
      &.ant-pagination-disabled {
        border-color: #e8e8e8;
        background: #f5f5f5;
        
        .anticon {
          color: ${COLORS.textMuted};
        }
        
        &:hover {
          border-color: #e8e8e8;
          background: #f5f5f5;
          transform: none;
          
          .anticon {
            color: ${COLORS.textMuted};
          }
        }
      }
    }
    
    .ant-pagination-jump-prev,
    .ant-pagination-jump-next {
      border-radius: 50% !important;
      border: 1.5px solid ${COLORS.border};
      min-width: 32px !important;
      height: 32px !important;
      line-height: 28px !important;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      
      .anticon {
        color: ${COLORS.textMuted};
        font-size: 14px;
      }
      
      &:hover {
        border-color: ${COLORS.$primaryBlue};
        background: rgba(24, 144, 255, 0.1);
        transform: translateY(-1px);
        
        .anticon {
          color: ${COLORS.$primaryBlue};
        }
      }
    }
  }
  
  /* Dropdown menu styling for pagination */
  .ant-select-dropdown {
    border-radius: 10px !important;
    border: 1px solid ${COLORS.border} !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
    padding: 4px !important;
    
    .ant-select-item {
      border-radius: 8px !important;
      margin: 2px 0 !important;
      padding: 8px 12px !important;
      color: ${COLORS.textDark} !important;
      min-height: 32px !important;
      font-size: 13px !important;
      line-height: 1.4 !important;
      display: flex !important;
      align-items: center !important;
      
      &:hover {
        background-color: rgba(24, 144, 255, 0.08) !important;
        color: ${COLORS.$primaryBlue} !important;
      }
      
      &.ant-select-item-option-selected {
        background-color: ${COLORS.$primaryBlue} !important;
        color: white !important;
        font-weight: 600 !important;
        
        .ant-select-item-option-state {
          color: white !important;
        }
      }
    }
    
    .ant-select-item-option-state {
      color: ${COLORS.$primaryBlue} !important;
      font-weight: bold !important;
    }
  }
`;

export const StyledTableContainer = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid ${COLORS.border};
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  
  /* Animation support */
  animation-fill-mode: both;
`;

export const RiskTag = styled(Tag)`
  font-weight: 600;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 12px;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const DetailModal = styled(Modal)`
  /* Modal positioning is handled by global CSS in modalResponsive.css */
  /* This component can be used for additional modal-specific styling */

  .ant-modal-header {
    
    .ant-modal-title {
      font-size: 20px;
      font-weight: 600;
      color: ${COLORS.textDark};
    }
  }
  
  .ant-modal-body {
    overflow-y: auto;
  }
  
  .ant-modal-footer {
    border-top: none;
    padding: 0;
  }
`;

export const LoadingContainer = styled.div`
  animation: ${fadeIn} 0.3s ease-out forwards;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  
  &.loading-fade-in {
    animation: ${fadeIn} 0.3s ease-out forwards;
  }
  
  &.loading-fade-out {
    animation: ${fadeIn} 0.3s ease-out reverse;
  }
`;
